import { useState, useCallback, useEffect } from 'react';
import { getPlanningReport, exportPlanningReport } from '../planningService';
import { formatDateToYyyyMmDd } from '../../../utils/formatDate';
import { isICCSupport } from '../../auth';

const MAX_DAYS = 90;

const todayAtMidnight = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
};

export function useFutureReadinessReport() {
    const [startDate, setStartDate] = useState(todayAtMidnight);
    const [endDate, setEndDate] = useState(() => {
        const d = todayAtMidnight();
        d.setDate(d.getDate() + 7);
        return d;
    });
    const [selectedTeamIds, setSelectedTeamIds] = useState([]);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [exporting, setExporting] = useState(false);
    const [teamOptions, setTeamOptions] = useState([]);
    const [teamsLoading, setTeamsLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const loadTeams = async () => {
            setTeamsLoading(true);
            try {
                // No team_ids filter: response reflects the user's full market-wide team scope.
                const result = await getPlanningReport({
                    start_date: formatDateToYyyyMmDd(todayAtMidnight()),
                    end_date: formatDateToYyyyMmDd(todayAtMidnight()),
                });
                if (!cancelled) {
                    const list = result?.teams || [];
                    setTeamOptions(list.map(t => ({ key: t.id, value: t.id, text: t.name })));
                }
            } catch { /* non-critical; team filter is optional */ }
            finally { if (!cancelled) setTeamsLoading(false); }
        };
        loadTeams();
        return () => { cancelled = true; };
    }, []);

    const validateDateRange = useCallback(() => {
        if (!startDate || !endDate) return 'Please select both a start date and end date.';
        if (endDate < startDate) return 'End date must be on or after the start date.';
        const diffDays = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24));
        if (diffDays > MAX_DAYS) return `Date range cannot exceed ${MAX_DAYS} days.`;
        return '';
    }, [startDate, endDate]);

    const buildParams = useCallback(() => {
        const params = {
            start_date: formatDateToYyyyMmDd(startDate),
            end_date: formatDateToYyyyMmDd(endDate),
        };
        if (selectedTeamIds.length > 0) {
            params.team_ids = selectedTeamIds.join(',');
        }
        return params;
    }, [startDate, endDate, selectedTeamIds]);

    const generateReport = useCallback(async () => {
        const validationError = validateDateRange();
        if (validationError) {
            setError(validationError);
            return;
        }
        setLoading(true);
        setError('');
        setData(null);
        try {
            const result = await getPlanningReport(buildParams());
            setData(result);
        } catch (err) {
            setError(err.message || 'An unexpected error occurred.');
        } finally {
            setLoading(false);
        }
    }, [buildParams, validateDateRange]);

    const exportReport = useCallback(async () => {
        const validationError = validateDateRange();
        if (validationError) {
            setError(validationError);
            return;
        }
        setExporting(true);
        setError('');
        try {
            await exportPlanningReport(buildParams());
        } catch (err) {
            setError(err.message || 'Export failed. Please try again.');
        } finally {
            setExporting(false);
        }
    }, [buildParams, validateDateRange]);

    return {
        startDate, setStartDate,
        endDate, setEndDate,
        selectedTeamIds, setSelectedTeamIds,
        teamOptions,
        teamsLoading,
        data,
        loading,
        error, setError,
        exporting,
        generateReport,
        exportReport,
    };
}
